import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, PhoneCall, MapPin, LifeBuoy } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api, formatApiErrorDetail } from "../lib/auth";
import { toast } from "sonner";
import { PUBLIC_CONTACT } from "../constants/testIds";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/forms/contact", form);
      setSent(true);
      toast.success("Message received. We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Submission failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={PUBLIC_CONTACT.page} className="bg-white">
      <Header />
      <main id="main-content">
        {/* Hero */}
        <section
          data-testid={PUBLIC_CONTACT.hero}
          className="pt-28 lg:pt-32 pb-14 lg:pb-16 bg-white"
        >
          <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-12">
            <p className="btc-eyebrow mb-4">CONTACT</p>
            <h1 className="font-display font-extrabold tracking-tight leading-[1.05] text-[40px] sm:text-[52px] lg:text-[64px] text-[#0B132B] mb-6">
              Talk to <span className="text-[#1A5FAB]">BTC.</span>
            </h1>
            <div className="h-[3px] w-16 bg-[#2D9B4E] rounded-full mb-6" />
            <p className="text-lg text-[#4B5563] leading-relaxed max-w-3xl">
              For partnerships, media, volunteering enquiries or general questions. If you or
              someone you love needs support, please use <Link to="/support" data-testid={PUBLIC_CONTACT.supportLink} className="text-[#23803F] font-semibold underline decoration-[#2D9B4E]/40 underline-offset-4">Get Support</Link> — a BTC team member will reach out within 24 hours.
            </p>
          </div>
        </section>

        <section className="bg-[#F8F9FB] py-16 lg:py-24">
          <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-14 items-start">
            {/* Left: contact channels */}
            <div className="space-y-6">
              {[
                { icon: LifeBuoy, t: "Need support?", d: "Get Support is the fastest path. A real BTC team member will respond within 24 hours.", to: "/support", cta: "Go to Get Support" },
                { icon: Mail, t: "General enquiries", d: "Use the form for partnerships, press, or anything that isn't a support request." },
                { icon: MapPin, t: "United Kingdom HQ", d: "Bonded Thru Cancer · United Kingdom · Serving patients worldwide." },
                { icon: PhoneCall, t: "24/7 Support Line", d: "Trained listeners are available every hour of every day for anyone facing cancer." },
              ].map((c) => (
                <div key={c.t} className="flex gap-4 items-start bg-white rounded-2xl border border-gray-100 p-5">
                  <div className="w-11 h-11 rounded-xl bg-[#2D9B4E]/10 flex items-center justify-center shrink-0">
                    <c.icon className="w-5 h-5 text-[#23803F]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="font-bold text-[#0B132B] mb-1">{c.t}</div>
                    <div className="text-sm text-[#4B5563] leading-relaxed">{c.d}</div>
                    {c.to && (
                      <Link to={c.to} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#1A5FAB]">
                        {c.cta} <ArrowRight className="w-3 h-3" />
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Right: form */}
            <div className="bg-white rounded-3xl p-8 lg:p-10 border border-gray-100">
              {sent ? (
                <div className="text-center py-8">
                  <Mail className="w-12 h-12 text-[#23803F] mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-[#0B132B] mb-2">Thank you.</h3>
                  <p className="text-[#4B5563] mb-6">Your message reached BTC. We'll respond shortly.</p>
                  <Link to="/" className="btc-btn-outline">Back to home</Link>
                </div>
              ) : (
                <form data-testid={PUBLIC_CONTACT.form} onSubmit={onSubmit} className="space-y-4">
                  <h3 className="text-xl font-bold text-[#0B132B] mb-2">Send us a message</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                      data-testid={PUBLIC_CONTACT.name}
                      required
                      placeholder="Your name"
                      value={form.name}
                      onChange={setField("name")}
                      className="px-4 py-3 rounded-xl bg-[#F8F9FB] border border-gray-200 focus:border-[#2D9B4E] focus:outline-none"
                    />
                    <input
                      data-testid={PUBLIC_CONTACT.email}
                      type="email"
                      required
                      placeholder="Email"
                      value={form.email}
                      onChange={setField("email")}
                      className="px-4 py-3 rounded-xl bg-[#F8F9FB] border border-gray-200 focus:border-[#2D9B4E] focus:outline-none"
                    />
                  </div>
                  <input
                    data-testid={PUBLIC_CONTACT.subject}
                    placeholder="Subject (optional)"
                    value={form.subject}
                    onChange={setField("subject")}
                    className="w-full px-4 py-3 rounded-xl bg-[#F8F9FB] border border-gray-200 focus:border-[#2D9B4E] focus:outline-none"
                  />
                  <textarea
                    data-testid={PUBLIC_CONTACT.message}
                    required
                    placeholder="How can we help?"
                    rows={5}
                    value={form.message}
                    onChange={setField("message")}
                    className="w-full px-4 py-3 rounded-xl bg-[#F8F9FB] border border-gray-200 focus:border-[#2D9B4E] focus:outline-none resize-none"
                  />
                  <button
                    data-testid={PUBLIC_CONTACT.submit}
                    type="submit"
                    disabled={busy}
                    className="w-full btc-btn-primary justify-center disabled:opacity-60"
                  >
                    {busy ? "Sending..." : "Send message"}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
